import styled, { css } from 'styled-components';
import {
    textAreaRoot,
    textFieldBaseStyles,
    hbFieldStateBorder,
    getCustomThemeDSContainer,
    getCustomThemeDSInput,
    HBTagsRow
} from '../HBTextField/HBTextField.styles';
import { ICustomThemeInput } from '../HBTextField/HBTextField.types';
import TextAreaBase from './TextAreaBase';
import { printCss } from '../../styles/mixins';
import spacing from '../../styles/spacing';
import { fontSizes } from '../../styles/fonts';
import {
    TTextFieldSize,
    DS_FIELD_BORDER_RADIUS
} from '../HBTextField/variables';
import { vars } from '../../theme';

export const TextArea = styled(TextAreaBase)`
    && {
        ${props => textFieldBaseStyles(props)}
        ${textAreaRoot}
        height: ${props => props.height};
        padding: 12px;
        white-space: pre-wrap;
        word-break: break-word;

        ${printCss(`
            word-break: break-all;
        `)}
    }
`;

// ─── DS Mode ────────────────────────────────────────────────────────────────

// innerHeight = minHeight - 2px border - 20px vertical padding (10px top + 10px bottom)
const DS_SIZE_PROPS: Record<
    TTextFieldSize,
    {
        minHeight: string;
        fontSize: string;
        lineHeight: string;
        innerHeight: string;
    }
> = {
    xs: {
        minHeight: '54px',
        fontSize: fontSizes.s14,
        lineHeight: '18px',
        innerHeight: '32px'
    },
    sm: {
        minHeight: '54px',
        fontSize: fontSizes.s14,
        lineHeight: '18px',
        innerHeight: '32px'
    },
    md: {
        minHeight: '64px',
        fontSize: fontSizes.s16,
        lineHeight: '22px',
        innerHeight: '42px'
    },
    lg: {
        minHeight: '74px',
        fontSize: fontSizes.s18,
        lineHeight: '26px',
        innerHeight: '52px'
    },
    xl: {
        minHeight: '74px',
        fontSize: fontSizes.s18,
        lineHeight: '26px',
        innerHeight: '52px'
    }
};

export { HBTagsRow };

// DSFieldContainer owns the border, background, padding, and resize handle.
// It does NOT scroll — DSScrollWrapper inside it owns scrolling.
// This separation keeps the padding fixed so it never scrolls away, preventing
// partial tag chips from appearing flush against the top border.
export const DSFieldContainer = styled.div<{
    $size: TTextFieldSize;
    $focused?: boolean;
    $error?: boolean;
    $disabled?: boolean;
    $resizable?: boolean;
    $customTheme?: ICustomThemeInput;
}>`
    && {
        box-sizing: border-box;
        width: 100%;
        height: ${({ $size }) => DS_SIZE_PROPS[$size].minHeight};
        padding: 10px 0 10px ${spacing[12]};
        border-radius: ${({ $size }) => DS_FIELD_BORDER_RADIUS[$size]};
        background: ${vars.colors.surface.item};
        overflow: hidden;
        resize: ${({ $resizable }) => ($resizable ? 'vertical' : 'none')};
        cursor: text;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;

        ${({ $focused, $error, $disabled }) =>
            hbFieldStateBorder({
                $focused,
                $error,
                $disabled,
                $disabledBorderColor: vars.colors.stroke.disabled
            })}

        ${({ $customTheme, $error, $disabled, $focused }) =>
            $customTheme &&
            getCustomThemeDSContainer(
                $customTheme,
                $error,
                $disabled,
                $focused
            )}

        ${({ $disabled }) =>
            $disabled &&
            css`
                color: ${vars.colors.text.disabled};
                -webkit-text-fill-color: ${vars.colors.text.disabled};
                resize: none;
            `}
    }
`;

// DSScrollWrapper is the actual scroll container. It lives inside DSFieldContainer
// and fills the content area (max-height: 100%). Because the padding belongs to
// DSFieldContainer (not here), scrolling never pushes padding out of view.
export const DSScrollWrapper = styled.div`
    overflow-y: auto;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    gap: ${spacing[4]};
    width: 100%;
`;

export const DSInnerTextArea = styled.textarea<{
    $size: TTextFieldSize;
    $customTheme?: ICustomThemeInput;
}>`
    && {
        all: unset;
        display: block;
        box-sizing: border-box;
        width: 100%;
        flex: 1 1 0;
        min-height: ${({ $size }) => DS_SIZE_PROPS[$size].innerHeight};
        overflow: auto;
        font-family: ${vars.fonts.body};
        font-size: ${({ $size }) => DS_SIZE_PROPS[$size].fontSize};
        line-height: ${({ $size }) => DS_SIZE_PROPS[$size].lineHeight};
        color: ${vars.colors.text.primary};
        resize: none;
    }

    &&::placeholder {
        color: ${vars.colors.text.tertiary};
    }

    ${({ $customTheme }) => $customTheme && getCustomThemeDSInput($customTheme)}
`;
